import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { RoomSummary } from '../models/Room';
import { ParticipantDetails, CreateRoomBookingResponse, RoomBookingStatus } from '../models/RoomBooking';
import { DiscountSettings, PaymentMethod } from '../models/Organization';
import type { CancellationPolicy } from '../models/CancellationPolicy';
import { AdditionalGear, RequestedAdditionalGear } from '../models/AdditionalGear';
import type { PassWalletSummary } from '../models/Pass';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { authService } from '../services/AuthService';
import { userApiClient } from '../userApiClient';
import { apiClient } from '../publicApiClient';
import { getBookingAnalyticsPayload, trackAbandonBooking, trackBookingConfirmed, trackBookingStep, trackLoginForBooking } from '../utils/analytics';
import { fetchEligibleWalletPasses } from '../utils/passEligibility';
import type { PassEligibilityEmptyReason } from '../utils/passEligibility';
import { formatWallClockInstant } from '../utils/wallClockInstant';
import type { PassTender } from './passes/PassTenderPicker';

// Import step components
import TimeAndDateStep from './booking-steps/TimeAndDateStep';
import AdditionalGearStep from './booking-steps/AdditionalGearStep';
import PaymentDetailsStep from './booking-steps/PaymentDetailsStep';
import WhoIsComingStep from './booking-steps/WhoIsComingStep';
import BookingConfirmationStep from './booking-steps/BookingConfirmationStep';
import PayStep from './booking-steps/PayStep';
import { getBookingSlotMismatchErrorMessage } from '../utils/bookingSlotErrors';
import { getSingleBookingDiscountedTotal } from '../utils/discounts';
import { roomBookPagePath } from '../utils/publicSpaceResolution';
import { buildRoomBookingOccurrences } from '../utils/bookingRepeatSlots';
import { serializeBookingInstantForApi } from '../utils/bookingInstant';
import BookingGearScopeBar from './booking/BookingGearScopeBar';
import {
  alignSessionGears,
  cloneGearSelection,
  gearAllowedOnSession,
  gearSelectionKey,
  gearsOnEverySession,
  sessionsShareGear,
} from '../utils/sessionGearSelection';

// Step labels are computed dynamically to optionally include the Pay step
type RoomBookingStepKey =
  | 'timeAndDate'
  | 'additionalGear'
  | 'paymentOptions'
  | 'whoIsComing'
  | 'pay'
  | 'confirmation';

interface RoomBookingStepperDialogProps {
  open: boolean;
  onClose: () => void;
  room: RoomSummary;
  spaceName?: string;
  initialDateTime?: Date;
  initialDuration?: number;
  initialStepKey?: RoomBookingStepKey | null;
}

const ROOM_BOOKING_STEP_ORDER: RoomBookingStepKey[] = [
  'timeAndDate',
  'additionalGear',
  'paymentOptions',
  'whoIsComing',
  'pay',
  'confirmation',
];

const getFallbackStepKey = (
  missingStepKey: RoomBookingStepKey,
  stepKeys: RoomBookingStepKey[]
): RoomBookingStepKey => {
  const missingStepIndex = ROOM_BOOKING_STEP_ORDER.indexOf(missingStepKey);
  const nextStep = ROOM_BOOKING_STEP_ORDER
    .slice(missingStepIndex + 1)
    .find((stepKey) => stepKeys.includes(stepKey));

  if (nextStep) {
    return nextStep;
  }

  const previousStep = [...ROOM_BOOKING_STEP_ORDER]
    .slice(0, missingStepIndex)
    .reverse()
    .find((stepKey) => stepKeys.includes(stepKey));

  return previousStep ?? stepKeys[0] ?? 'timeAndDate';
};

export default function RoomBookingStepperDialog({
  open,
  onClose,
  room,
  spaceName,
  initialDateTime,
  initialDuration,
  initialStepKey,
}: RoomBookingStepperDialogProps) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { city, spaceName: spaceNameSlug } = useParams<{ city?: string; spaceName?: string }>();
  
  // Calculate responsive height based on screen dimensions
  const getDialogHeight = () => {
    if (fullScreen) return '100svh';
    
    const screenHeight = window.innerHeight;
    
    // For screens with height 800 or less
    if (screenHeight <= 800) {
      return '98vh';
    }
    
    // Default for larger screens
    return '70vh';
  };
  const [activeStepKey, setActiveStepKey] = useState<RoomBookingStepKey>('timeAndDate');
  const [addSessionControls, setAddSessionControls] = useState<{
    canAdd: boolean;
    addSession: () => void;
  } | null>(null);
  /** -1 edits gear for every session. Zero or more edits that session only. */
  const [gearFocusIndex, setGearFocusIndex] = useState(-1);
  const [sessionGearCatalogs, setSessionGearCatalogs] = useState<AdditionalGear[][]>([]);
  const [isLoggedIn] = useState<boolean>(authService.getIsLoggedIn());

  // Booking state
  const [bookingData, setBookingData] = useState({
    date: initialDateTime || new Date(),
    time: initialDateTime || new Date(),
    duration: initialDuration || 60,
    recurringWeeks: undefined as number | undefined,
    sessionStarts: [] as Date[],
    sessionGears: [] as RequestedAdditionalGear[][],
    selectedAdditionalGears: [] as RequestedAdditionalGear[],
    paymentMethod: undefined as PaymentMethod | undefined,
    participantDetails: {
      bandName: '',
      participantCount: 1,
      details: '',
    } as ParticipantDetails,
  });

  const [created, setCreated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bookingResponse, setBookingResponse] = useState<CreateRoomBookingResponse | null>(null);
  const [createdBookingStatus, setCreatedBookingStatus] = useState<RoomBookingStatus | undefined>(undefined);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [spaceRegulation, setSpaceRegulation] = useState<string | undefined>(undefined);
  const [fetchedSpaceName, setFetchedSpaceName] = useState<string | undefined>(undefined);
  const [spaceDiscountSettings, setSpaceDiscountSettings] = useState<DiscountSettings | null>(null);
  const [spaceCancellationPolicy, setSpaceCancellationPolicy] = useState<CancellationPolicy | null>(null);
  const [regulationAccepted, setRegulationAccepted] = useState(false);
  const wasOpenRef = useRef(false);
  const lastTrackedStepRef = useRef<RoomBookingStepKey | null>(null);
  const openingStepRef = useRef<RoomBookingStepKey | null>(null);
  const gearRequestIdRef = useRef(0);
  const [availableGear, setAvailableGear] = useState<AdditionalGear[]>([]);

  // Money | Pass tender choice. Pass mode is XOR with paymentMethod and disables gears/recurrence.
  const [tender, setTender] = useState<PassTender>('money');
  const [selectedPassId, setSelectedPassId] = useState<string | null>(null);
  const [eligiblePasses, setEligiblePasses] = useState<PassWalletSummary[]>([]);
  const [eligiblePassesLoading, setEligiblePassesLoading] = useState(false);
  const [passEligibilityEmptyReason, setPassEligibilityEmptyReason] =
    useState<PassEligibilityEmptyReason | null>(null);
  const passEligibilityRequestIdRef = useRef(0);

  // Calculate available time for gear fetching
  const availableFromTimeISO = useMemo(() => {
    if (!bookingData.date || !bookingData.time) return '';
    const dt = new Date(bookingData.date);
    dt.setHours(bookingData.time.getHours(), bookingData.time.getMinutes(), 0, 0);
    return dt.toISOString();
  }, [bookingData.date, bookingData.time]);

  /** Picker wall-clock start for cancellation deadlines (space timezone), not UTC from toISOString. */
  const bookingWallClockStart = useMemo(() => {
    if (!bookingData.date || !bookingData.time) return '';
    const dt = new Date(bookingData.date);
    dt.setHours(bookingData.time.getHours(), bookingData.time.getMinutes(), 0, 0);
    return formatWallClockInstant(dt, 'seconds');
  }, [bookingData.date, bookingData.time]);

  const bookingTimezone = room.timezone ?? room.spaceTimeZone;

  const loadAvailableGear = useCallback(async () => {
    const starts = bookingData.sessionStarts.length > 0
      ? bookingData.sessionStarts
      : availableFromTimeISO
        ? [new Date(availableFromTimeISO)]
        : [];
    if (!room.id || !bookingData.duration || starts.length === 0) {
      setAvailableGear([]);
      setSessionGearCatalogs([]);
      return [] as AdditionalGear[];
    }

    const requestId = ++gearRequestIdRef.current;

    try {
      const catalogs = await Promise.all(starts.map(async (start) => {
        try {
          return await apiClient.getAvailableRoomGears(
            room.id,
            serializeBookingInstantForApi(start),
            bookingData.duration,
          );
        } catch (error) {
          console.error('Failed to fetch available gear:', error);
          return null;
        }
      }));

      if (requestId !== gearRequestIdRef.current) {
        return catalogs.find((catalog): catalog is AdditionalGear[] => catalog != null) ?? [];
      }

      const merged = new Map<string, AdditionalGear>();
      catalogs.forEach((catalog) => {
        catalog?.forEach((gear) => merged.set(gearSelectionKey(gear), gear));
      });
      const gearData = Array.from(merged.values());
      setAvailableGear(gearData);
      setSessionGearCatalogs((previous) => catalogs.map((catalog, index) => catalog ?? previous[index] ?? []));
      setBookingData((prev) => {
        const aligned = alignSessionGears(
          prev.sessionStarts,
          starts,
          prev.sessionGears,
          prev.selectedAdditionalGears,
        );
        const sessionGears = aligned.map((list, index) =>
          gearAllowedOnSession(list, catalogs[index]),
        );
        const selectedAdditionalGears = sessionGears[0] ?? gearAllowedOnSession(prev.selectedAdditionalGears, catalogs[0]);
        const unchanged = sessionGears.length === prev.sessionGears.length
          && sessionGears.every((list, index) => list.length === prev.sessionGears[index]?.length
            && list.every((gear, gearIndex) => gear.id === prev.sessionGears[index][gearIndex]?.id
              && gear.source === prev.sessionGears[index][gearIndex]?.source))
          && selectedAdditionalGears.length === prev.selectedAdditionalGears.length
          && selectedAdditionalGears.every((gear, index) =>
            gear.id === prev.selectedAdditionalGears[index]?.id
            && gear.source === prev.selectedAdditionalGears[index]?.source);
        return unchanged ? prev : { ...prev, sessionGears, selectedAdditionalGears };
      });
      return gearData;
    } catch (error) {
      if (requestId !== gearRequestIdRef.current) {
        return [] as AdditionalGear[];
      }
      console.error('Failed to fetch available gear:', error);
      setAvailableGear([]);
      setSessionGearCatalogs([]);
      return [] as AdditionalGear[];
    }
  }, [room.id, availableFromTimeISO, bookingData.duration, bookingData.sessionStarts]);

  // Set default payment method
  useEffect(() => {
    if (bookingData.paymentMethod === undefined && room.supportedPaymentMethods) {
      const methods = room.supportedPaymentMethods || [];
      const defaultMethod = methods.includes(PaymentMethod.ONLINE)
        ? PaymentMethod.ONLINE
        : methods[0];
      setBookingData(prev => ({ ...prev, paymentMethod: defaultMethod }));
    }
  }, [room.supportedPaymentMethods, bookingData.paymentMethod]);

  useEffect(() => {
    if (!open) {
      return;
    }

    void loadAvailableGear();
  }, [open, loadAvailableGear]);

  // Fetch ACTIVE wallet passes eligible for this room, re-checked whenever duration changes
  // (room passes are MINUTE-unit, so eligibility depends on remaining balance vs. duration).
  useEffect(() => {
    if (!open || !room.id) {
      setEligiblePasses([]);
      setPassEligibilityEmptyReason(null);
      setEligiblePassesLoading(false);
      return;
    }

    const requestId = ++passEligibilityRequestIdRef.current;
    setEligiblePassesLoading(true);
    fetchEligibleWalletPasses({
      targetType: 'ROOM',
      targetId: room.id,
      requiredMinutes: bookingData.duration,
    })
      .then((result) => {
        if (requestId !== passEligibilityRequestIdRef.current) return;
        setEligiblePasses(result.eligiblePasses);
        setPassEligibilityEmptyReason(result.emptyReason);
      })
      .finally(() => {
        if (requestId !== passEligibilityRequestIdRef.current) return;
        setEligiblePassesLoading(false);
      });
  }, [open, room.id, bookingData.duration]);

  // Drop tender/selection when it's no longer valid (e.g. duration change made the pass insufficient).
  // Preselect when exactly one eligible pass remains.
  useEffect(() => {
    if (eligiblePasses.length === 0) {
      setTender('money');
      setSelectedPassId(null);
      return;
    }
    setSelectedPassId((prev) => {
      if (prev && eligiblePasses.some((p) => p.id === prev)) return prev;
      return eligiblePasses.length === 1 ? eligiblePasses[0].id : null;
    });
  }, [eligiblePasses]);

  const handleTenderChange = useCallback((nextTender: PassTender) => {
    setTender(nextTender);
    if (nextTender === 'pass') {
      // Room + pass: gears and recurrence are not supported.
      setSelectedPassId(eligiblePasses.length === 1 ? eligiblePasses[0].id : null);
      setGearFocusIndex(-1);
      setBookingData((prev) => ({
        ...prev,
        selectedAdditionalGears: [],
        sessionGears: [],
        recurringWeeks: undefined,
        sessionStarts: [],
      }));
    }
  }, [eligiblePasses]);

  const hasAdditionalGearStep = tender !== 'pass' && availableGear.length > 0;
  const stepKeys = useMemo<RoomBookingStepKey[]>(() => {
    const visibleStepKeys: RoomBookingStepKey[] = ['timeAndDate'];

    if (hasAdditionalGearStep) {
      visibleStepKeys.push('additionalGear');
    }

    visibleStepKeys.push('paymentOptions', 'whoIsComing');

    if (tender === 'money' && bookingData.paymentMethod === PaymentMethod.ONLINE) {
      visibleStepKeys.push('pay');
    }

    visibleStepKeys.push('confirmation');
    return visibleStepKeys;
  }, [hasAdditionalGearStep, bookingData.paymentMethod, tender]);

  useEffect(() => {
    if (stepKeys.includes(activeStepKey)) {
      return;
    }

    setActiveStepKey(getFallbackStepKey(activeStepKey, stepKeys));
  }, [activeStepKey, stepKeys]);

  const stepLabels = stepKeys.map((stepKey) => {
    switch (stepKey) {
      case 'timeAndDate':
        return t('bookingSteps.timeAndDate', 'Time & Date');
      case 'additionalGear':
        return t('bookingSteps.additionalGear', 'Rent Additional Gear');
      case 'paymentOptions':
        return t('bookingSteps.paymentOptions', 'Payment Options');
      case 'whoIsComing':
        return t('bookingSteps.whoIsComing', 'Who is Coming');
      case 'pay':
        return t('bookingSteps.pay', 'Pay');
      case 'confirmation':
        return t('bookingSteps.confirmation', 'Booking Confirmation');
      default:
        return '';
    }
  });

  const stepIndex = stepKeys.indexOf(activeStepKey);
  const safeActiveStep = stepIndex >= 0 ? stepIndex : 0;
  const isPayStep = activeStepKey === 'pay';
  const discountFooterText = useMemo(() => {
    const single = spaceDiscountSettings?.singleBookingDiscount;
    if (!single?.enabled) return null;
    return t(
      'payment.singleBookingDiscountAppliedWithThreshold',
      '{{percent}}% discount for {{hours}}h+ bookings',
      {
        percent: single.discountPercent,
        hours: single.minimumBookingHours,
      }
    );
  }, [spaceDiscountSettings, t]);
  const showSingleStepDiscountChip =
    activeStepKey === 'timeAndDate' &&
    (!bookingData.recurringWeeks || bookingData.recurringWeeks <= 1) &&
    Boolean(discountFooterText);

  const goToNextStep = useCallback(() => {
    const currentStepIndex = stepKeys.indexOf(activeStepKey);
    const nextStepKey = stepKeys[Math.min(currentStepIndex + 1, stepKeys.length - 1)];
    if (nextStepKey) {
      setActiveStepKey(nextStepKey);
    }
  }, [activeStepKey, stepKeys]);

  const handleNext = useCallback(async () => {
    if (activeStepKey === 'timeAndDate') {
      const gearData = await loadAvailableGear();
      setActiveStepKey(gearData.length > 0 ? 'additionalGear' : 'paymentOptions');
      return;
    }

    goToNextStep();
  }, [activeStepKey, goToNextStep, loadAvailableGear]);

  const handleBack = useCallback(() => {
    const currentStepIndex = stepKeys.indexOf(activeStepKey);
    const previousStepKey = stepKeys[Math.max(currentStepIndex - 1, 0)];
    if (previousStepKey) {
      setActiveStepKey(previousStepKey);
    }
  }, [activeStepKey, stepKeys]);

  // Fetch space regulation when dialog has room
  useEffect(() => {
    const fetchSpace = async () => {
      if (!room?.spaceId || !open) return;
      try {
        const space = await apiClient.getSpaceSummary(room.spaceId);
        const resolvedDiscountSettings =
          space.discountSettings ??
          (space as unknown as { organization?: { discountSettings?: DiscountSettings | null } })?.organization?.discountSettings ??
          null;
        setSpaceRegulation(space.regulation);
        setFetchedSpaceName(space.name);
        setSpaceDiscountSettings(resolvedDiscountSettings);
        setSpaceCancellationPolicy(space.cancellationPolicy ?? null);
        console.debug('[RoomBookingStepperDialog] Loaded space summary', {
          spaceId: room.spaceId,
          discountSettings: resolvedDiscountSettings,
          rawDiscountSettings: (space as any)?.discountSettings ?? null,
          nestedOrgDiscountSettings: (space as any)?.organization?.discountSettings ?? null,
        });
        if (!space.regulation?.trim()) {
          setRegulationAccepted(true);
        }
      } catch (err) {
        console.error('Failed to fetch space regulation:', err);
      }
    };
    fetchSpace();
  }, [room?.spaceId, open]);

  // Reset state when dialog opens (after being closed)
  // This prevents visual blip during closing animation
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      // Dialog is opening - reset state while it's still transitioning
      const openingStep = initialStepKey || 'timeAndDate';
      openingStepRef.current = openingStep;
      setActiveStepKey(openingStep);
      setCreated(false);
      setError(null);
      setBookingResponse(null);
      setCreatedBookingStatus(undefined);
      setIsPaymentProcessing(false);
      setSpaceRegulation(undefined);
      setFetchedSpaceName(undefined);
      setSpaceDiscountSettings(null);
      setSpaceCancellationPolicy(null);
      setRegulationAccepted(false);
      setAvailableGear([]);
      setSessionGearCatalogs([]);
      setGearFocusIndex(-1);
      setTender('money');
      setSelectedPassId(null);
      setEligiblePasses([]);
      setBookingData({
        date: initialDateTime || new Date(),
        time: initialDateTime || new Date(),
        duration: initialDuration || 60,
        recurringWeeks: undefined,
        sessionStarts: [],
        sessionGears: [],
        selectedAdditionalGears: [],
        paymentMethod: undefined,
        participantDetails: {
          bandName: '',
          participantCount: 1,
          details: '',
        },
      });
    }
    wasOpenRef.current = open;
  }, [open, initialDateTime, initialDuration, initialStepKey]);

  // Must run after the open-reset effect so the first event uses the opening step.
  useEffect(() => {
    if (!open) {
      lastTrackedStepRef.current = null;
      openingStepRef.current = null;
      return;
    }

    const openingStep = openingStepRef.current;
    if (openingStep && activeStepKey !== openingStep) return;
    openingStepRef.current = null;

    if (lastTrackedStepRef.current === activeStepKey) return;
    lastTrackedStepRef.current = activeStepKey;

    const stepIndex = stepKeys.indexOf(activeStepKey);
    trackBookingStep({
      provider_type: 'space',
      provider_id: room.spaceId ?? room.id,
      city: city ?? undefined,
      step: activeStepKey,
      ...(stepIndex >= 0 ? { step_index: stepIndex } : {}),
    });
  }, [open, activeStepKey, city, room.id, room.spaceId, stepKeys]);

  const handleBookingConfirmed = (booking: {
    date: Date;
    time: Date;
    duration: number;
    recurringWeeks?: number;
    sessionStarts?: Date[];
  }) => {
    setBookingData((prev) => {
      const nextSessions = booking.sessionStarts ?? prev.sessionStarts;
      const sameSessions =
        nextSessions.length === prev.sessionStarts.length &&
        nextSessions.every((start, index) => start.getTime() === prev.sessionStarts[index]?.getTime());
      const sameSlot =
        prev.date.getTime() === booking.date.getTime() &&
        prev.time.getTime() === booking.time.getTime() &&
        prev.duration === booking.duration &&
        prev.recurringWeeks === booking.recurringWeeks &&
        sameSessions;
      if (sameSlot) return prev;
      const sessionGears = alignSessionGears(
        prev.sessionStarts,
        nextSessions,
        prev.sessionGears,
        prev.selectedAdditionalGears,
      );
      return {
        ...prev,
        date: booking.date,
        time: booking.time,
        duration: booking.duration,
        recurringWeeks: booking.recurringWeeks,
        sessionStarts: nextSessions,
        sessionGears,
        selectedAdditionalGears: sessionGears[0] ?? prev.selectedAdditionalGears,
      };
    });
    setGearFocusIndex((current) => {
      const count = booking.sessionStarts?.length ?? 0;
      if (count < 2) return -1;
      return current >= count ? count - 1 : current;
    });
  };

  const handleGearSelectionChange = (selectedAdditionalGears: RequestedAdditionalGear[]) => {
    setBookingData((prev) => {
      const starts = prev.sessionStarts;
      if (starts.length < 2 || gearFocusIndex < 0) {
        const next = cloneGearSelection(selectedAdditionalGears);
        return {
          ...prev,
          selectedAdditionalGears: next,
          sessionGears: starts.map(() => cloneGearSelection(next)),
        };
      }
      const index = Math.min(gearFocusIndex, starts.length - 1);
      const sessionGears = (prev.sessionGears.length === starts.length
        ? prev.sessionGears
        : starts.map(() => prev.selectedAdditionalGears)
      ).map((list, listIndex) => (listIndex === index ? cloneGearSelection(selectedAdditionalGears) : list));
      return {
        ...prev,
        sessionGears,
        selectedAdditionalGears: index === 0 ? cloneGearSelection(selectedAdditionalGears) : sessionGears[0] ?? prev.selectedAdditionalGears,
      };
    });
  };

  const handlePaymentMethodChange = (paymentMethod: PaymentMethod) => {
    setBookingData(prev => ({ ...prev, paymentMethod }));
  };

  const handleParticipantDetailsChange = useCallback((participantDetails: ParticipantDetails) => {
    setBookingData(prev => ({ ...prev, participantDetails }));
  }, []);

  const parseCreateBookingStatus = (rawStatus?: string): RoomBookingStatus | undefined => {
    if (!rawStatus) return undefined;
    const normalized = rawStatus.trim().replace(/[-\s]/g, '_').toUpperCase();
    if (normalized === 'CONFIRMED') {
      return RoomBookingStatus.CONFIRMED_BY_HOST;
    }
    return (Object.values(RoomBookingStatus) as string[]).includes(normalized)
      ? (normalized as RoomBookingStatus)
      : undefined;
  };

  const redirectAnonymousUserToLogin = useCallback(() => {
    const dt = new Date(bookingData.date);
    dt.setHours(bookingData.time.getHours(), bookingData.time.getMinutes(), 0, 0);
    const spacePathName = spaceName?.trim() || fetchedSpaceName || spaceNameSlug;
    const redirectUrl =
      city && spacePathName
        ? roomBookPagePath(city, spacePathName, room.name)
        : location.pathname;
    trackLoginForBooking({
      provider_type: 'space',
      provider_id: room.spaceId ?? room.id,
      city: city ?? undefined,
    });
    navigate('/login', {
      state: {
        redirectUrl,
        openBookingRoomId: room.id,
        openRoomBooking: true,
        roomAvailableFromTime: dt.toISOString(),
        durationInMinutes: bookingData.duration,
      },
    });
  }, [
    bookingData.date,
    bookingData.duration,
    bookingData.time,
    city,
    fetchedSpaceName,
    location.pathname,
    navigate,
    room.id,
    room.name,
    room.spaceId,
    spaceName,
    spaceNameSlug,
  ]);

  const handleSubmit = async () => {
    try {
      if (!bookingData.date || !bookingData.time) return;
      if (tender === 'money' && !bookingData.paymentMethod) return;
      if (tender === 'pass' && !selectedPassId) return;

      const dt = new Date(bookingData.date);
      dt.setHours(bookingData.time.getHours(), bookingData.time.getMinutes(), 0, 0);
      const seriesStarts =
        tender === 'money' && bookingData.sessionStarts.length > 1 ? bookingData.sessionStarts : [dt];
      const seriesGears = tender === 'pass'
        ? seriesStarts.map(() => [])
        : seriesStarts.map((_, index) =>
            bookingData.sessionGears[index] ?? bookingData.selectedAdditionalGears,
          );
      const occurrences =
        tender === 'money'
          ? buildRoomBookingOccurrences(
              seriesStarts,
              bookingData.duration,
              serializeBookingInstantForApi,
              seriesStarts.length > 1 ? seriesGears : null,
            )
          : undefined;
      
      const hasRegulation = Boolean(spaceRegulation?.trim());
      const response = await userApiClient.createRoomBooking({
        roomId: room.id,
        startTime: occurrences?.[0]?.startTime ?? serializeBookingInstantForApi(dt),
        durationInMinutes: bookingData.duration,
        // Room + pass disables gears and recurrence; XOR paymentMethod/passId — never send both.
        additionalGears: tender === 'pass' ? [] : seriesGears[0] ?? bookingData.selectedAdditionalGears,
        ...(tender === 'pass' ? { passId: selectedPassId } : { paymentMethod: bookingData.paymentMethod }),
        participantDetails: bookingData.participantDetails,
        regulationAccepted: hasRegulation ? regulationAccepted : undefined,
        ...(occurrences ? { occurrences } : {}),
        analytics: getBookingAnalyticsPayload(),
      });
      setBookingResponse(response);
      setCreatedBookingStatus(parseCreateBookingStatus(response.status));
      const occurrenceCount = occurrences?.length ?? 1;
      const hours = bookingData.duration / 60;
      const gearTotal =
        tender === 'pass'
          ? 0
          : seriesGears.reduce((sum, list) => sum + list.reduce((sessionSum, selected) => {
              const gear = availableGear.find((item) => item.id === selected.id && item.source === selected.source);
              return sessionSum + (gear?.pricePerH ?? 0) * hours;
            }, 0), 0);
      const baseTotal = Number(room.pricePerH) * hours * occurrenceCount + gearTotal;
      const bookingValue =
        tender === 'pass'
          ? 0
          : getSingleBookingDiscountedTotal(baseTotal, bookingData.duration, spaceDiscountSettings, occurrenceCount);
      trackBookingConfirmed({
        provider_type: 'space',
        provider_id: room.spaceId ?? room.id,
        booking_id: response.id,
        city: city ?? undefined,
        currency: String(room.currecyCode ?? 'PLN'),
        booking_value: bookingValue,
        payment_method: tender === 'pass' ? 'pass' : String(bookingData.paymentMethod ?? '').toLowerCase(),
      });

      if (tender === 'money' && bookingData.paymentMethod === PaymentMethod.ONLINE) {
        // Move to Pay step
        setActiveStepKey('pay');
      } else {
        // Cash, other methods, or pass: go straight to confirmation (pass never opens Stripe PayStep)
        setCreated(true);
        setActiveStepKey('confirmation');
      }
    } catch (e: unknown) {
      setError(
        getBookingSlotMismatchErrorMessage(
          e,
          t,
          'bookingSteps.saveError',
          'Error creating booking',
        ),
      );
    }
  };

  const handleClose = () => {
    if (!bookingResponse && !created && activeStepKey !== 'confirmation') {
      trackAbandonBooking({
        provider_type: 'space',
        provider_id: room.spaceId ?? room.id,
        city: city ?? undefined,
        step: activeStepKey,
        ...(stepIndex >= 0 ? { step_index: stepIndex } : {}),
      });
    }
    onClose();
  };

  const handlePaymentSubmit = () => {
    const form = document.getElementById('payment-form') as HTMLFormElement | null;
    if (form) {
      form.requestSubmit();
    }
  };

  const renderScrollableStep = (content: React.ReactNode) => (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {content}
    </Box>
  );

  const getStepContent = (stepKey: RoomBookingStepKey) => {
    switch (stepKey) {
      case 'timeAndDate':
        return (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <TimeAndDateStep
              room={room}
              bookingSlotsConfig={room.bookingSlotsConfig}
              initialDateTime={initialDateTime}
              initialDuration={initialDuration}
              showRecurringControls={tender !== 'pass'}
              onValidityChange={() => {}} // We'll handle validation differently
              onBookingConfirmed={handleBookingConfirmed}
              onAddSessionControlsChange={setAddSessionControls}
            />
          </Box>
        );
      case 'additionalGear': {
        const series = bookingData.sessionStarts.length > 1;
        const focusedCatalog = series && sessionGearCatalogs.length > 0
          ? sessionGearCatalogs[gearFocusIndex < 0 ? 0 : Math.min(gearFocusIndex, sessionGearCatalogs.length - 1)] ?? availableGear
          : availableGear;
        const displayedGears = !series || gearFocusIndex < 0
          ? (series ? gearsOnEverySession(bookingData.sessionGears) : bookingData.selectedAdditionalGears)
          : bookingData.sessionGears[gearFocusIndex] ?? [];
        const sessionLabels = bookingData.sessionStarts.map((start) => {
          const day = start.toLocaleDateString(i18n.language || 'en', { month: 'short', day: 'numeric' });
          const time = start.toLocaleTimeString(i18n.language || 'en', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          });
          return `${day}, ${time}`;
        });
        return renderScrollableStep(
          <>
            {series ? (
              <BookingGearScopeBar
                focusIndex={gearFocusIndex}
                varies={!sessionsShareGear(bookingData.sessionGears)}
                sessionLabels={sessionLabels}
                onFocusIndexChange={setGearFocusIndex}
              />
            ) : null}
            <AdditionalGearStep
              roomId={room.id}
              availableFromTime={availableFromTimeISO}
              durationInMinutes={bookingData.duration}
              selectedAdditionalGears={displayedGears}
              onSelectionChange={handleGearSelectionChange}
              availableGear={focusedCatalog}
            />
          </>
        );
      }
      case 'paymentOptions':
        return renderScrollableStep(
          <PaymentDetailsStep
            room={room}
            duration={bookingData.duration}
            selectedAdditionalGears={bookingData.selectedAdditionalGears}
            availableFromTime={availableFromTimeISO}
            availableGear={availableGear}
            selectedPaymentMethod={bookingData.paymentMethod}
            onPaymentMethodChange={handlePaymentMethodChange}
            recurringWeeks={
              bookingData.sessionStarts.length > 1
                ? bookingData.sessionStarts.length
                : bookingData.recurringWeeks
            }
            sessionStarts={bookingData.sessionStarts}
            sessionGears={bookingData.sessionGears}
            discountSettings={spaceDiscountSettings}
            cancellationPolicy={room.cancellationPolicy ?? spaceCancellationPolicy}
            timezone={bookingTimezone}
            cancellationStartTime={bookingWallClockStart}
            eligiblePasses={eligiblePasses}
            eligiblePassesLoading={eligiblePassesLoading}
            passEligibilityEmptyReason={passEligibilityEmptyReason}
            tender={tender}
            onTenderChange={handleTenderChange}
            selectedPassId={selectedPassId}
            onPassIdChange={setSelectedPassId}
            spaceRegulation={spaceRegulation}
            spaceName={spaceName?.trim() || fetchedSpaceName}
            regulationAccepted={regulationAccepted}
            onRegulationAcceptedChange={setRegulationAccepted}
          />
        );
      case 'whoIsComing':
        return renderScrollableStep(
          <WhoIsComingStep
            onNext={handleNext}
            onBack={handleBack}
            onParticipantDetailsChange={handleParticipantDetailsChange}
            onSignIn={redirectAnonymousUserToLogin}
          />
        );
      case 'pay':
        if (tender === 'money' && bookingData.paymentMethod === PaymentMethod.ONLINE && bookingResponse?.stripePaymentIntentClientSecret) {
          return renderScrollableStep(
            <PayStep
              clientSecret={bookingResponse.stripePaymentIntentClientSecret}
              stripeChargeType={bookingResponse.stripeChargeType}
              stripeAccountId={bookingResponse.stripeAccountId}
              onPaymentSuccess={() => {
                setCreated(true);
                setActiveStepKey('confirmation');
              }}
              onPaymentError={(err) => {
                setError(err);
              }}
              isProcessing={isPaymentProcessing}
              setIsProcessing={setIsPaymentProcessing}
            />
          );
        }
        return renderScrollableStep(
          <BookingConfirmationStep
            paymentMethod={bookingData.paymentMethod}
            paymentStatus={created ? 'completed' : 'pending'}
            bookingStatus={createdBookingStatus}
            bookingId={bookingResponse?.id}
            isRecurringBooking={Boolean(bookingResponse?.recurringSeriesId)}
            passId={bookingResponse?.passId}
            passBookingState={bookingResponse?.passBookingState}
          />
        );
      case 'confirmation':
        return renderScrollableStep(
          <BookingConfirmationStep
            paymentMethod={bookingData.paymentMethod}
            paymentStatus={created ? 'completed' : 'pending'}
            bookingStatus={createdBookingStatus}
            bookingId={bookingResponse?.id}
            isRecurringBooking={Boolean(bookingResponse?.recurringSeriesId)}
            passId={bookingResponse?.passId}
            passBookingState={bookingResponse?.passBookingState}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  const isStepValid = (stepKey: RoomBookingStepKey): boolean => {
    switch (stepKey) {
      case 'timeAndDate':
        return Boolean(bookingData.date && bookingData.time && bookingData.duration);
      case 'additionalGear':
        return true; // Gear selection is optional
      case 'paymentOptions': {
        const paymentOk =
          tender === 'pass' ? Boolean(selectedPassId) : Boolean(bookingData.paymentMethod);
        const hasRegulation = Boolean(spaceRegulation?.trim());
        const regulationOk = !hasRegulation || regulationAccepted;
        return paymentOk && regulationOk;
      }
      case 'whoIsComing':
        return Boolean(bookingData.participantDetails.participantCount > 0);
      default:
        return true;
    }
  };

  const canProceed = (stepKey: RoomBookingStepKey): boolean => {
    const isValid = isStepValid(stepKey);
    const authOk = stepKey !== 'whoIsComing' || isLoggedIn;
    const canProceedResult = isValid && authOk;
    
    // Debug logging
    if (stepKey === 'whoIsComing') {
      console.log('Who is coming validation:', {
        bandName: bookingData.participantDetails.bandName,
        participantCount: bookingData.participantDetails.participantCount,
        isValid,
        authOk,
        canProceedResult
      });
    }
    
    return canProceedResult;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="md" 
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          height: getDialogHeight(),
          maxHeight: getDialogHeight(),
          width: fullScreen ? '100vw' : '90vw',
          maxWidth: fullScreen ? '100vw' : '900px',
          borderRadius: fullScreen ? 0 : 2,
          overflow: 'hidden',
        }
      }}
    >
      
      <DialogContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
        {/* Top Header with Room Info + Stepper */}
        <Box sx={{ 
          p: { xs: 1.5, md: 2 }, 
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: { xs: 1, md: 2 },
        }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0,
              flexShrink: 0,
              maxWidth: { xs: '28%', sm: '22%', md: '30%' },
            }}
          >
            <Typography 
              variant="subtitle1" 
              sx={{ fontWeight: 700, minWidth: 0 }}
              noWrap
              title={`${room.name}${spaceName ? ' — ' + spaceName : ''}`}
            >
              {room.name}
            </Typography>
            {spaceName && (
              <Typography 
                variant="body2" 
                sx={{ color: 'text.secondary', minWidth: 0, display: { xs: 'none', sm: 'block' } }}
                noWrap
                title={spaceName}
              >
                • {spaceName}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stepper
              activeStep={safeActiveStep}
              alternativeLabel
              sx={{
                '& .MuiStepConnector-line': { minWidth: { xs: 6, md: 10 } },
                '& .MuiStepConnector-root': { top: { xs: 7, md: 9 } },
                '& .MuiStep-root': { px: { xs: 0.25, md: 0.5 } },
              }}
            >
            {(stepLabels || []).map((label, index) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    '& .MuiStepLabel-label': {
                      display: 'block',
                      fontSize: { xs: '0.625rem', md: '0.75rem' },
                      lineHeight: 1.2,
                      textAlign: 'center',
                      whiteSpace: 'normal',
                      fontWeight: safeActiveStep === index ? 700 : 400,
                      color: safeActiveStep === index ? 'text.primary' : 'text.secondary',
                      mt: 0.25,
                    },
                    '& .MuiStepLabel-label.Mui-active': {
                      color: 'text.primary',
                      fontWeight: 700,
                    },
                    '& .MuiStepLabel-label.Mui-completed': {
                      color: 'text.secondary',
                      fontWeight: 400,
                    },
                    '& .MuiStepLabel-iconContainer': {
                      '& .MuiSvgIcon-root': {
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                        width: { xs: 18, md: 20 },
                        height: { xs: 18, md: 20 },
                      },
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
            </Stepper>
          </Box>
          <IconButton aria-label="close" onClick={handleClose} sx={{ flexShrink: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content Area */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'hidden', 
          pt: activeStepKey === 'additionalGear' && bookingData.sessionStarts.length > 1
            ? 0.5
            : { xs: 1, md: 2 },
          px: { xs: 1, md: 2 },
          pb: { xs: 1, md: 2 },
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}>
          <Box sx={{ maxWidth: 1000, mx: 'auto', width: '100%', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            {getStepContent(activeStepKey)}
          </Box>
        </Box>

        {/* Bottom Navigation */}
        <Box sx={{ 
          px: { xs: 1.5, md: 2 },
          pt: { xs: 1.25, md: 1.5 },
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: { xs: 'calc(env(safe-area-inset-bottom) + 12px)', md: 2 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                {['additionalGear', 'paymentOptions', 'whoIsComing'].includes(activeStepKey) && (
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{ borderRadius: 999, py: { xs: 1, md: 1.1 }, px: { xs: 2, md: 3 } }}
                  >
                    {t('common.back', 'Back')}
                  </Button>
                )}
                {activeStepKey === 'timeAndDate' && addSessionControls?.canAdd ? (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addSessionControls.addSession}
                    sx={{
                      borderRadius: 999,
                      py: { xs: 1, md: 1.1 },
                      px: { xs: 1.75, md: 2.25 },
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {t('booking.addAnotherSession', 'Add session')}
                  </Button>
                ) : null}
          </Box>

          {showSingleStepDiscountChip ? (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                px: 0.5,
              }}
            >
              <LocalOfferIcon sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'success.main',
                  fontWeight: 700,
                  textAlign: 'center',
                  lineHeight: 1.3,
                }}
              >
                {discountFooterText}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0 }}>
            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}

            {activeStepKey !== 'confirmation' && (
              <Button
                variant="contained"
                color="primary"
                onClick={
                  activeStepKey === 'whoIsComing'
                    ? handleSubmit
                    : (isPayStep ? handlePaymentSubmit : handleNext)
                }
                disabled={!canProceed(activeStepKey) || (isPayStep && isPaymentProcessing)}
                sx={{ 
                  borderRadius: 999, 
                  py: { xs: 1.1, md: 1.25 }, 
                  px: { xs: 3, md: 4 },
                  fontWeight: 700
                }}
              >
                {activeStepKey === 'whoIsComing'
                  ? t('spaceCalendar.book', 'REZERWUJ')
                  : isPayStep
                    ? (isPaymentProcessing ? t('payment.processing', 'Processing...') : t('payment.payNow', 'Pay Now'))
                    : t('common.next', 'Next')}
              </Button>
            )}

            {activeStepKey === 'confirmation' && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleClose}
                sx={{ borderRadius: 999, py: { xs: 1.1, md: 1.25 }, px: { xs: 3, md: 4 }, fontWeight: 700 }}
              >
                {t('common.close', 'Close')}
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
